import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles as SparklesIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import Seo from "@/components/Seo";
import SocialLinks from "@/components/SocialLinks";
import { StoryFacilitator } from "@/components/StoryFacilitator";

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

const Chapter = ({ title, children, testid }) => (
    <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        variants={fadeUp}
        className="mt-20"
        data-testid={testid}
    >
        <h2 className="font-serif text-3xl sm:text-4xl mb-8 leading-tight">{title}</h2>
        <div className="space-y-6 text-base sm:text-lg text-foreground/80 leading-relaxed">
            {children}
        </div>
    </motion.div>
);

export default function StoryPage() {
    return (
        <div data-testid="story-page">
            <Seo
                title="Mon histoire · Foundation of Light"
                description="Le parcours d'Olivier Masurel, de la vie ordinaire à l'appel spirituel, jusqu'à la naissance de la Foundation of Light et de ses soins de lumière."
                path="/histoire"
            />
            {/* Hero */}
            <section className="relative overflow-hidden bg-[#0b1424]">
                <div className="absolute inset-0 pointer-events-none">
                    <img
                        src="/brand/olivier-portrait.jpg"
                        alt="Portrait d'Olivier Masurel, fondateur de Foundation of Light"
                        className="absolute inset-0 w-full h-full object-cover opacity-45 grayscale-[15%]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-[#0b1424]/60 via-[#0b1424]/70 to-[#0b1424]" />
                </div>
                <div className="relative max-w-4xl mx-auto px-6 py-32 text-center">
                    <div className="text-xs tracking-[0.4em] uppercase text-amber-200/80 mb-6 font-medium">Mon histoire</div>
                    <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl leading-tight text-white">
                        Là où le chemin<br />
                        <span className="italic text-amber-200">m'a conduit.</span>
                    </h1>
                    <p
                        className="mt-8 text-sm sm:text-base tracking-[0.1em] text-amber-100/80 font-light"
                        itemScope
                        itemType="https://schema.org/Person"
                        data-testid="story-byline"
                    >
                        Par <strong itemProp="name" className="text-amber-100 font-medium">Olivier Masurel</strong>
                        <span className="text-amber-100/60"> · fondateur de la Foundation of Light</span>
                        <meta itemProp="jobTitle" content="Fondateur · Praticien en soins énergétiques & langage de lumière" />
                        <meta itemProp="url" content="https://foundation-of-light.com/histoire" />
                    </p>
                </div>
            </section>

            {/* Body */}
            <section className="relative overflow-hidden bg-background">
                <div className="absolute inset-0 pointer-events-none opacity-[0.06]">
                    <div className="absolute top-40 -left-20 w-96 h-96 rounded-full bg-secondary blur-3xl" />
                    <div className="absolute bottom-40 -right-20 w-[28rem] h-[28rem] rounded-full bg-primary blur-3xl" />
                </div>
                <div className="relative max-w-3xl mx-auto px-6 pt-20 pb-24">

                    {/* Opening */}
                    <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="space-y-6 text-base sm:text-lg text-foreground/80 leading-relaxed">
                        <p>
                            Il est difficile de raconter un chemin spirituel sans lui donner, après coup, une apparence
                            de logique. Pourtant, lorsque je regarde aujourd'hui les années qui ont précédé la naissance
                            de la Foundation of Light, je ne peux pas dire que j'avais un plan. J'ai simplement avancé,
                            étape après étape, en suivant ce qui se présentait à moi. Et, avec le recul, je découvre que
                            certains de ces passages semblaient préparer le suivant.
                        </p>
                    </motion.div>

                    <Chapter title="Les premiers seuils — 2012 à 2024" testid="chap-seuils">
                        <p>
                            Le 1er décembre 2012, j'apprends à ressentir physiquement l'énergie vitale puis je commence dans la foulée
                            à pratiquer quotidiennement le Qi Gong médical au sein de l'école Shaolin Wahnam.
                            À cette époque, je ne sais pas encore où cette pratique va me conduire à part m'aider à faire face aux difficultés de la vie. Je découvre
                            ainsi une autre manière de percevoir l'être humain, l'énergie, le corps et les
                            mouvements subtils qui peuvent se produire lorsque l'on apprend à être davantage
                            présent à soi-même.
                        </p>
                        <p>
                            Cette pratique ouvre progressivement en moi un espace nouveau. Un espace d'écoute.
                            Je comprends peu à peu que l'être humain ne se résume peut-être pas à ce qui est
                            immédiatement visible, mesurable ou compréhensible par le mental. Mais je ne cherche
                            pas encore à mettre des mots sur ce que je ressens. Le chemin se poursuit simplement.
                        </p>
                        <p>
                            À partir de 2014, un autre chemin d'exploration commence à prendre une place importante
                            dans ma vie : pendant près de dix années, je me forme à l'analyse des rêves, des signes
                            et des symboles. Je ne considère pas cette approche comme une manière de prédire l'avenir
                            ou de chercher des réponses toutes faites. Elle m'apprend plutôt à observer. À écouter ce
                            qui se manifeste derrière les images, les associations et les symboles. À accepter que
                            certaines expériences intérieures puissent avoir plusieurs niveaux de compréhension
                            et que leur sens ne soit pas toujours immédiat.
                        </p>
                        <p>
                            Avec le temps, les rêves sont ainsi devenus pour moi un espace d'écoute intérieure parmi
                            d'autres. Pas une vérité à imposer. Pas une croyance à adopter. Simplement une autre
                            manière de prêter attention à ce qui cherche parfois à se révéler à nous.
                        </p>
                    </Chapter>

                    <Chapter title="L'émergence du Langage de Lumière — été 2024" testid="chap-langage">
                        <p>
                            Autour de l'été 2024, quelque chose de nouveau commence à se manifester en moi :
                            le Langage de Lumière. Je ne l'avais pas décidé. Je ne cherchais pas à apprendre un
                            nouveau langage. Il est apparu progressivement, comme une forme d'expression qui semblait
                            venir d'un espace différent de celui dans lequel je m'exprime habituellement.
                        </p>
                        <p>
                            Après toutes ces années consacrées à l'observation des rêves, des signes et des symboles,
                            cette nouvelle expérience m'a invité à élargir encore ma manière d'écouter. J'ai commencé
                            à comprendre que la transmission pouvait parfois dépasser les mots. Le Langage de Lumière
                            est devenu pour moi une expérience de reconnexion : une manière de laisser circuler une
                            information, une vibration ou une intention sans chercher nécessairement à la traduire par
                            le mental.
                        </p>
                    </Chapter>

                    <Chapter title="Le rêve fondateur — décembre 2024" testid="chap-reve">
                        <p>
                            Quelques mois plus tard, en décembre 2024, j'ai reçu en rêve quelque chose qui allait
                            donner une nouvelle direction à mon chemin. Le rêve était en anglais. Et c'est ainsi
                            que j'ai reçu le nom <em>Foundation of Light</em>.
                        </p>
                        <p>
                            Je l'ai conservé dans sa forme originale. Ce détail a pour moi son importance.
                            Car même si les premières étapes de ce projet sont aujourd'hui orientées vers un espace
                            largement francophone, je ressens depuis son origine que ce qui cherche à naître ici
                            dépasse une langue, une culture ou un territoire particulier.
                        </p>
                        <p>
                            Je n'ai pas vécu ce rêve comme une injonction à créer une organisation au sens
                            conventionnel du terme. Je l'ai plutôt ressenti comme l'ouverture d'un espace.
                            Un lieu où des êtres qui ressentent en eux cet appel vers davantage de conscience
                            pourraient se retrouver, partager, recevoir et avancer ensemble. Un lieu où la Lumière
                            ne serait pas une croyance à défendre, mais une expérience intérieure à rencontrer.
                        </p>
                    </Chapter>

                    <Chapter title="Apprendre à recevoir plutôt qu'à faire" testid="chap-recevoir">
                        <p>
                            Au fil de ce parcours, une compréhension s'est également approfondie en moi. Elle rejoint
                            une dimension de la philosophie essénienne qui m'accompagne : celle de l'effacement
                            progressif de l'ego afin de permettre à une volonté plus grande de pouvoir se manifester
                            à travers nous.
                        </p>
                        <p>
                            Cela transforme profondément la manière d'envisager la transmission. Il ne s'agit plus
                            de se demander : « Que puis-je faire ? » mais plutôt : « Puis-je devenir suffisamment
                            disponible pour laisser passer ce qui cherche à se manifester ? »
                        </p>
                        <p>
                            Cette distinction est essentielle pour moi. Je ne me considère pas comme la source de
                            ce qui est transmis. Je me considère comme un être humain en chemin, qui apprend à
                            écouter, à recevoir et à servir au mieux ce qui lui est confié.
                        </p>
                    </Chapter>

                    <Chapter title="Le sceau — juillet 2025" testid="chap-sceau">
                        <p>
                            En juillet 2025, après une période durant laquelle j'avais réalisé de nombreuses
                            transmissions et soins de lumière auprès de différentes personnes, j'ai reçu un autre
                            rêve particulièrement marquant. Dans ce rêve, une chevalière portant le sceau des
                            guérisseurs intergalactiques m'était remise.
                        </p>
                        <p>
                            Après dix années consacrées à l'observation des rêves et des symboles, j'avais appris
                            à ne pas chercher immédiatement une interprétation définitive à ce type d'expérience.
                            Je n'ai donc pas vécu cette image comme un titre, une reconnaissance ou une forme de
                            distinction. Je l'ai ressentie comme un symbole de responsabilité — un rappel que les
                            expériences spirituelles ne sont pas là pour nourrir l'importance personnelle, mais
                            pour nous inviter à davantage de service, de présence et d'intégrité.
                        </p>
                    </Chapter>

                    <Chapter title="Un collectif de Lumière" testid="chap-collectif">
                        <p>
                            Dans cette évolution, je ressens également la présence et l'accompagnement d'un collectif
                            d'êtres de lumière. Je ne souhaite pas ici leur donner de noms, de catégories ou d'étiquettes.
                            La Foundation of Light n'a pas vocation à devenir une plateforme consacrée à une hiérarchie
                            d'êtres ou à une vision « galactique » du monde.
                        </p>
                        <p>
                            Ce qui m'est demandé est beaucoup plus simple : <em>créer et préserver un espace sacré</em>.
                            Un espace dans lequel chacun puisse venir avec son propre chemin, sa propre sensibilité et
                            ses propres questions — un espace où différentes formes de transmission, de conscience et
                            d'expérience peuvent se rencontrer sans avoir besoin d'être enfermées dans un système.
                        </p>
                    </Chapter>

                    <Chapter title="Pourquoi la Foundation of Light existe aujourd'hui" testid="chap-pourquoi">
                        <p>
                            Avec le temps, j'ai compris que la Foundation of Light n'était peut-être pas quelque chose
                            que je devais « construire » au sens habituel du terme. Elle est plutôt quelque chose que
                            je suis invité à servir. Je n'en suis pas la destination. Je n'en suis pas la source.
                            J'en suis simplement l'un des gardiens et des passeurs.
                        </p>
                        <p>
                            Ce lieu naît donc de mon propre parcours, mais il ne m'appartient pas. Il est ouvert à
                            celles et ceux qui ressentent l'appel d'une évolution intérieure, d'une conscience plus
                            vaste et d'une relation plus profonde avec la Lumière.
                        </p>
                        <p>
                            Les premières étapes de cette aventure seront principalement proposées en français.
                            Mais la vision qui a donné naissance à la Foundation of Light n'est pas francophone.
                            Dès le premier rêve, elle m'est apparue dans une autre langue. Peut-être était-ce simplement
                            une manière de me rappeler que ce qui est véritablement essentiel n'a pas de langue.
                        </p>
                    </Chapter>

                    <Chapter title="Un lieu pour se souvenir" testid="chap-souvenir">
                        <p>
                            Je ne souhaite pas promettre à qui que ce soit une transformation particulière.
                            Je ne souhaite pas convaincre. Je souhaite simplement ouvrir une porte.
                        </p>
                        <p>
                            Une porte vers un espace où chacun puisse ralentir, écouter et peut-être retrouver
                            quelque chose qu'il portait déjà en lui. Car au fond, je ne crois pas que nous ayons
                            à devenir quelqu'un d'autre pour nous rapprocher de la Lumière. Peut-être avons-nous
                            simplement à retirer progressivement ce qui nous empêche de la laisser se manifester.
                        </p>
                        <p>
                            C'est le chemin que j'ai moi-même commencé à parcourir en 2012. Et c'est là où ce chemin
                            m'a conduit aujourd'hui : à ouvrir un espace de Lumière, non pas pour dire aux autres où
                            aller, mais pour cheminer ensemble vers ce que chacun est appelé à devenir.
                        </p>
                    </Chapter>

                    <StoryFacilitator />

                    {/* Closing invitation */}
                    <motion.div
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, margin: "-80px" }}
                        variants={fadeUp}
                        className="mt-24 rounded-3xl border border-secondary/40 bg-gradient-to-br from-secondary/10 via-transparent to-primary/5 p-10 sm:p-14 text-center"
                        data-testid="story-closing"
                    >
                        <SparklesIcon className="w-8 h-8 text-secondary mx-auto mb-8" />
                        <p className="font-serif text-2xl sm:text-3xl italic text-foreground/90 mb-10">
                            Bienvenue à la Foundation of Light.
                        </p>
                        <div className="flex flex-wrap gap-3 justify-center">
                            <Link to="/evenements">
                                <Button className="rounded-full h-12 px-6 bg-primary text-primary-foreground hover:scale-[1.03] transition-transform" data-testid="story-cta-events">
                                    Découvrir les ateliers <ArrowRight className="ml-2 w-4 h-4" />
                                </Button>
                            </Link>
                            <Link to="/reservation">
                                <Button variant="outline" className="rounded-full h-12 px-6" data-testid="story-cta-booking">
                                    Réserver un soin
                                </Button>
                            </Link>
                        </div>
                        <div className="mt-10 pt-8 border-t border-border/40 flex flex-col items-center gap-3">
                            <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Cheminer ensemble</div>
                            <SocialLinks size="lg" testid="story-social-links" />
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
}
